import { Component, OnInit } from '@angular/core';
import { AgGridColumns, AgGridFilterParams, AgGridOptionsBuilder } from '../../Compliance/AgGrid';
import { ColDef, GridApi, GridReadyEvent } from 'ag-grid-community';
import { AgGridActionCellRendererComponent, AgGridActionCellRendererParams } from '../../Compliance/AgGrid/CellRenderers/agGridActionCellRenderer.component';
import { ValuationTemplateService } from './valuationTemplate.service';
import { WeissmanModalService } from '../../Compliance/WeissmanModalService';
import { AddEditValuationTemplateComponent } from './Add-Edit-Tempalate/addEditValuationTemplate.component';
import { UpgradeNavigationServiceHandler } from '../../Common/Routing/upgrade-navigation-handler.service';
import { HelpService } from '../../UI-Lib/Help-Tooltip';
import { VALUATION_TEMPLATE_HELP } from './valuationTemplate.help';
import { MessageModalService } from '../../UI-Lib/Message-Box/messageModal.service';
import { InstanceRights, RestrictService } from 'src/app/Common/Permissions/restrict.service';

@Component({
    selector: 'valuation-templates',
    templateUrl: './valuationTemplates.component.html',
    styleUrls: ['./valuationTemplates.component.scss'],
    standalone: false
})
export class ValuationTemplatesComponent implements OnInit {
    constructor(
        private readonly _valuationTemplateService: ValuationTemplateService,
        private readonly _modalService: WeissmanModalService,
        private readonly _helpService: HelpService,
        private readonly _messageModalService: MessageModalService,
        private readonly _routerService: UpgradeNavigationServiceHandler,
        private readonly _restrictService: RestrictService
    ) {
    }

    gridId: string = 'e34cb223-dd10-4ee8-890b-2caa2b83ef75';
    gridOptions = new AgGridOptionsBuilder()
        .withColumnPinning()
        .withSort()
        .withMultipleColumnSort()
        .withLoadingOverlay()
        .withColumnResize()
        .withContext(this)
        .build();
    gridApi: GridApi;
    hasValuationTemplateEdit: boolean;

    private _gridValues: Core.ValuationTemplateModel[] = [];

    ngOnInit(): void {
        this._helpService.setContent(VALUATION_TEMPLATE_HELP);
        this.hasValuationTemplateEdit = this._restrictService.hasInstanceRight(InstanceRights.VALUATIONTEMPLATEEDIT);
    }

    onAgGridReady(event: GridReadyEvent): void {
        this.gridApi = event.api;

        const columns: ColDef[] = [
            {
                headerName: 'Template Name',
                field: 'name',
                width: AgGridColumns.textColumnWidth,
                editable: false,
                lockVisible: true,
                lockPosition: true,
                suppressMovable: true,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams
            },
            {
                headerName: 'Template Description',
                field: 'description',
                width: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterWithBlankOptionsParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams
            },
            {
                headerName: 'States',
                field: 'states',
                width: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterWithBlankOptionsAndCommaParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                valueGetter: params => {
                    const template = params.data as Core.ValuationTemplateModel;
                    if (template.states.length) {
                        return template.states.map(x => x.stateAbbr).join(', ');
                    } else {
                        return 'National';
                    }
                }
            },
            {
                headerName: 'Assessors',
                field: 'assessors',
                width: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterWithBlankOptionsAndCommaParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                valueGetter: params => {
                    const template = params.data as Core.ValuationTemplateModel;
                    if (template.states.length === 1) {
                        return params.data.assessors.map(x => x.assessorName).join(', ');
                    } else {
                        return 'Using All Assessors';
                    }
                }
            },
            {
                headerName: 'Site Classes',
                field: 'siteClassPrimary',
                width: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterWithBlankOptionsAndCommaParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                valueGetter: params => {
                    return params.data.siteClassPrimaries.map(x => x.siteClassPrimaryDesc).join(', ');
                }
            },
            {
                colId: 'grid-column-actions',
                headerName: '',
                field: 'actions',
                width: AgGridColumns.getActionColumnWidth(2),
                minWidth: AgGridColumns.getActionColumnWidth(2),
                maxWidth: AgGridColumns.getActionColumnWidth(2),
                suppressSizeToFit: true,
                suppressAutoSize: true,
                resizable: false,
                suppressColumnsToolPanel: true,
                lockPinned: true,
                pinned: 'right',
                lockVisible: true,
                sortable: false,
                cellRenderer: AgGridActionCellRendererComponent,
                cellRendererParams: {
                    buttonConfigs: [
                        {
                            iconClass: 'fa-pencil',
                            isShown: () => this.hasValuationTemplateEdit,
                            onClick: this.editTemplate.bind(this)
                        },
                        {
                            iconClass: 'fa-trash',
                            buttonClass: 'btn-primary btn-danger',
                            isShown: () => this.hasValuationTemplateEdit,
                            onClick: this.deleteTemplate.bind(this)
                        }
                    ]
                } as AgGridActionCellRendererParams
            }
        ];

        this.gridApi.setGridOption('columnDefs', columns);
        this.refreshGridData();
    }

    async createNewTemplate(): Promise<void> {
        const result = await this._modalService.showAsync(AddEditValuationTemplateComponent, {}, 'modal-md');

        if (!result) {
            return;
        }
        this._routerService.go('valuationTemplateDetail', { templateId: result.valuationTemplateId });
    }

    editTemplate(gridRow: AgGridActionCellRendererParams): void {
        const rowData = gridRow.data as Core.ValuationTemplateModel;
        this._routerService.go('valuationTemplateDetail', { templateId: rowData.valuationTemplateId });
    }

    async deleteTemplate(gridRow: AgGridActionCellRendererParams): Promise<void> {
        const rowData = gridRow.data as Core.ValuationTemplateModel;

        try {
            await this._messageModalService.confirm('Are you sure you want to delete this valuation template?');
        } catch (err) {
            return;
        }

        try {
            await this._valuationTemplateService.deleteTemplate(rowData.valuationTemplateId);
        } finally {
            await this.refreshGridData();
        }
    }

    async refreshGridData(): Promise<void> {
        if (this.gridApi) {
            this.gridApi.setGridOption("loading", true);
        }
        try {
            this._gridValues = await this._valuationTemplateService.getTemplateList();
        } finally {
            this._setDataSource();
        }
    }

    private _setDataSource(): void {
        if (!this.gridApi) {
            return;
        }
        this.gridApi.setGridOption('rowData', this._gridValues);
        this.gridApi.setGridOption("loading", false);
    }
}
